import { useEffect, useState, useCallback } from 'react'
import { Card, CardContent, Grid, Hidden, Stack } from '@mui/material'
import FormSkeletonWrapper from '../../../SkeletonUI/FormSkeletonWrapper/FormSkeletonWrapper'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import SpreadsForm from './SpreadsForm'
import { toast } from 'react-toastify';
import { parseErrors, replaceNullValues } from '../../../../services/utils'
import PageTitle from '../../../Misc/PageTitle'
import axios from 'axios'
import MerchantMenu from '../MerchantMenu'
import MerchantAlias from '../MerchantAlias'
import MerchantSubmenu from '../MerchantSubmenu'
import useIsMounted from '../../../../hooks/useIsMounted'
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate'
import useHasPermissions from '../../../../hooks/useHasPermissions'
import { COUNTRY_SPREADS__UPDATE } from '../../../../enums/Caps'


export default function SpreadEdit() {
    const { t } = useTranslation()

    const hasPermissions = useHasPermissions()

    const navigate = useNavigate()

    const isMounted = useIsMounted()

    const api = useAxiosPrivate()

    const [data, setData] = useState()

    const [countries, setCountries] = useState()

    const { userId, spreadId } = useParams()

    const [isLoading, setLoading] = useState(true)

    const retrieveData = useCallback(() => {
        axios.all([
            api.get('/internal/countries/'),
            api.get(`/internal/merchants/${userId}/spreads/${spreadId}/`)
        ])
            .then(axios.spread((...responses) => {
                if (isMounted()) {
                    const countriesResponse = responses[0]
                    const spreadResponse = responses[1]

                    const country_list = countriesResponse.data.data.sort((a, b) => (a.name_es > b.name_es) ? 1 : ((b.name_es > a.name_es) ? -1 : 0))

                    setCountries(
                        country_list.map((c) => ({
                            id: c.code,
                            name: c.name_es
                        }))
                    )
                    setData(replaceNullValues(spreadResponse.data.data))
                }
            }))
            .catch(() => isMounted() && toast.error(t('Something went wrong')))
            .finally(() => isMounted() && setLoading(false))
    }, [spreadId, userId, t, api, isMounted])

    const handleSubmit = (values, { setFieldError }) => {
        api.put(`/internal/merchants/${userId}/spreads/${spreadId}/`, values)
            .then(() => {
                if (isMounted()) {
                    toast.success(t('The data has been updated correctly'))
                    navigate(`/merchants/${userId}/spreads`)
                }
            })
            .catch(({ response }) => {
                if (isMounted()) {
                    const errors = parseErrors(response)

                    if (typeof errors === 'object') {
                        for (let i in errors) {
                            setFieldError(errors[i].field, t(errors[i].message), false)
                        }
                    } else if (typeof errors === 'string') {
                        toast.error(t(errors))
                    } else {
                        toast.error(t('Something went wrong'))
                    }
                }
            })
    }

    const handleCancel = () => {
        navigate(`/merchants/${userId}/spreads`)
    }

    const FORM_VALIDATION = Yup.object().shape({
        country: Yup.string()
            .test('len', t('You must select a valid country'), val => val.length === 2)
            .required(t('This field is required')),
        spread: Yup.number()
            .min(0, t('Spread value must be greater than or equal to zero'))
            .required(t('This field is required')),
    })

    useEffect(() => {
        const init = () => {
            retrieveData()
        }

        init()
    }, [retrieveData])

    return (
        <>
            <Card>
                <CardContent>
                    <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <PageTitle title={t('Edit spread')} />

                        <Hidden xlUp={true}>
                            {userId ? <MerchantMenu id={userId} /> : ''}
                        </Hidden>
                    </Stack>

                    <MerchantAlias />

                    <Grid container spacing={2}>
                        <Grid item xs={12} xl={2}>
                            <MerchantSubmenu current='spreads' id={userId} />
                        </Grid>
                        <Grid item xs={12} xl={10}>
                            {
                                isLoading ? <FormSkeletonWrapper /> :
                                    <SpreadsForm
                                        isNew={false}
                                        handleSubmit={handleSubmit}
                                        handleCancel={handleCancel}
                                        initialFormState={data}
                                        FORM_VALIDATION={FORM_VALIDATION}
                                        merchant_id={userId}
                                        countries={countries}
                                        hasPermissions={hasPermissions(COUNTRY_SPREADS__UPDATE)}
                                    />
                            }
                        </Grid>
                    </Grid>

                </CardContent>
            </Card>
        </>
    )
}
